import { formatOrgDate } from '../../lib/utils';
import { LIVE_DEMO_ORG_LABEL_OVERRIDES, MAX_HERO_SCENARIOS } from './constants';

function normalizeOrgKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, ' ');
}

export function getLiveDemoOrgLabel(orgName) {
  const displayName = String(orgName || '').trim();
  if (!displayName) return 'your organization';
  return LIVE_DEMO_ORG_LABEL_OVERRIDES[normalizeOrgKey(displayName)] || displayName;
}

export function getDefaultHeroSubheadline(orgName, domain) {
  const target = String(domain || '').trim() || getLiveDemoOrgLabel(orgName);
  return `Running live on ${target}. The answer adapts to each member.`;
}

export function shouldUseDefaultSubheadline(value, orgName) {
  const subheadline = String(value || '').trim();
  if (!subheadline) return true;

  const normalizedSubheadline = normalizeOrgKey(subheadline);
  const orgLabel = String(orgName || '').trim();
  const candidates = [
    // Previous auto-generated subhead — treat as the default so it gets replaced.
    `Show how members can get grounded answers from live ${orgLabel} content and see different answers when member context changes.`,
  ]
    .map(normalizeOrgKey)
    .filter(Boolean);

  return candidates.includes(normalizedSubheadline);
}

export function findSharedScenarioQuestion(scenarios) {
  const usableScenarios = Array.isArray(scenarios) ? scenarios.slice(0, 2) : [];
  if (usableScenarios.length < 2) return '';

  const firstQuestions = new Set(
    (usableScenarios[0]?.demo_questions || [])
      .map((question) => String(question).trim())
      .filter(Boolean),
  );
  for (const question of usableScenarios[1]?.demo_questions || []) {
    const normalized = String(question).trim();
    if (normalized && firstQuestions.has(normalized)) {
      return normalized;
    }
  }
  return '';
}

function normalizeHeroScenarioPreview(scenario, index) {
  const question = String(scenario?.question || '').trim();
  const answerParagraphs = Array.isArray(scenario?.answer_paragraphs)
    ? scenario.answer_paragraphs
        .map((paragraph) => String(paragraph || '').trim())
        .filter(Boolean)
        .slice(0, 4)
    : [];
  const answer = String(scenario?.answer || '').trim() || answerParagraphs.join(' ');
  if (!question || !answer) return null;

  const displayName =
    String(scenario?.display_name || scenario?.label || 'Demo member').trim() || 'Demo member';
  const scenarioCode = String(scenario?.code || '').trim() || null;
  const fallbackKey =
    displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-') || `scenario-${index + 1}`;

  return {
    key: `hero-${index + 1}-${scenarioCode || fallbackKey}`,
    scenarioCode,
    displayName,
    firstName: displayName.split(' ')[0] || displayName,
    avatarText: displayName
      .split(' ')
      .slice(0, 2)
      .map((part) => part[0] || '')
      .join('')
      .toUpperCase(),
    hint: String(scenario?.hint || scenario?.status_label || 'Demo member').trim() || 'Demo member',
    question,
    answer,
    answerParagraphs: answerParagraphs.length > 0 ? answerParagraphs : [answer],
    isRisk: scenario?.is_risk === true,
    statusLabel:
      String(scenario?.status_label || '').trim() ||
      (scenario?.is_risk === true ? 'Needs attention' : 'On track'),
  };
}

export function buildHeroScenarioPreviews(rawScenarios) {
  if (!Array.isArray(rawScenarios)) return [];

  return rawScenarios
    .map((scenario, index) => normalizeHeroScenarioPreview(scenario, index))
    .filter(Boolean)
    .slice(0, MAX_HERO_SCENARIOS);
}

export function formatKnowledgeTrustDate(value, clientConfig = null) {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  // Same rule as the date on the home page: the organisation's language and
  // clock, not the visitor's browser's.
  return formatOrgDate(parsed, clientConfig, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
